import { useState, useRef, useEffect, useId, useCallback } from "react";
import { createPortal } from "react-dom";
import { usePopoverPosition } from "../hooks/usePopoverPosition";
import "./SelectPopover.css";

/**
 * Roughly one option row, used only to place the menu on its first paint — the real height is
 * measured as soon as it mounts. See `usePopoverPosition`.
 */
const OPTION_HEIGHT_ESTIMATE = 37;

interface SelectPopoverProps {
  /** `readonly` so a shared `as const` option table can be passed straight in. */
  options: readonly string[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  fullWidth?: boolean;
  /**
   * Lands on the trigger button so a sibling `<label htmlFor>` can name it. The listbox borrows
   * that name through `aria-labelledby`, so it is announced as the same control.
   */
  id?: string;
  /**
   * `id` of hint text that explains the control - most usefully why it is currently disabled.
   * Without this the hint is visible but unannounced.
   */
  describedById?: string;
  /** Lands on the trigger button as `data-testid`, so tests need not match label copy. */
  testId?: string;
  /**
   * Whether the menu offers a row that clears back to the placeholder.
   *
   * True by default, because most callers are optional filters. Pass false for a required choice —
   * a page size, say — where the placeholder is not a value the control can legitimately hold and
   * picking it would send `""` to the caller.
   */
  clearable?: boolean;
}


/**
 * A single-select dropdown: a button that opens a portalled listbox.
 *
 * Keyboard model — the APG "collapsible listbox", chosen because it keeps the trigger a plain
 * button (so every caller's `<label htmlFor>` and accessible name are untouched) while putting
 * `aria-activedescendant` somewhere it is valid. ARIA 1.2 does not allow it on a button, axe's
 * aria-allowed-attr flags it there, and VoiceOver does not follow it, so the highlighted row went
 * unannounced.
 *
 * - Opened from the keyboard (Enter, Space, ArrowDown, ArrowUp on the trigger), DOM focus moves to
 *   the listbox, which names the highlighted row with `aria-activedescendant`. Arrows, Home and End
 *   move; Enter or Space chooses and closes; Escape closes. Each hands focus back to the trigger.
 * - Opened with the pointer, focus stays on the trigger and nothing is highlighted, as it always
 *   was. An arrow pressed then takes focus into the listbox, stepping from the current choice.
 * - Tab closes the menu and puts focus back on the trigger during the keydown, without preventing
 *   it, so the browser's own Tab step then moves on from the trigger: to the next control, or
 *   through an enclosing dialog's focus trap exactly as a Tab from the trigger would.
 */
export const SelectPopover = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  fullWidth,
  id,
  describedById,
  testId,
  clearable = true,
}: SelectPopoverProps) => {
  const [open, setOpen] = useState(false);
  /**
   * Which row the keyboard is on, as an index into `rows` (the reset row first, when there is one),
   * or -1 for none. Set every time the menu opens and only read while it is open, so no close path
   * has to reset it.
   */
  const [activeIndex, setActiveIndex] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  /** A keyboard open asked for focus in the listbox before the listbox had mounted. */
  const focusListOnMountRef = useRef(false);
  /**
   * Set when Space has just chosen a row. Focus is back on the trigger by the time Space comes up,
   * and a button fires its click on Space's keyup, which would reopen the menu the keydown had just
   * closed. Cleared by the next keydown or blur on the trigger, so a keyup that lands somewhere else
   * cannot leave it set to swallow a later, genuine press.
   */
  const swallowSpaceUpRef = useRef(false);
  const baseId = useId();
  const listId = `${baseId}-listbox`;
  const triggerId = id ?? `${baseId}-trigger`;
  const optionId = (index: number) => `${listId}-option-${index}`;

  /** Every row the menu shows, in order: `""` is the reset row back to the placeholder. */
  const rows: readonly string[] = clearable ? ["", ...options] : options;
  const selectedIndex = rows.indexOf(value);

  /**
   * A row index kept inside `rows`. With no rows at all the upper bound is -1, so this comes out as
   * -1 — "no row" — rather than pointing `aria-activedescendant` at an option that does not exist.
   */
  const clampRow = (index: number) => Math.min(Math.max(index, 0), rows.length - 1);

  const openFromKeyboard = (fallback: "first" | "last") => {
    // Start on the current choice, so ArrowDown then Enter never silently changes it.
    setActiveIndex(
      clampRow(selectedIndex >= 0 ? selectedIndex : fallback === "first" ? 0 : rows.length - 1),
    );
    focusListOnMountRef.current = true;
    setOpen(true);
  };

  const openFromPointer = () => {
    setActiveIndex(-1);
    setOpen(true);
  };

  /**
   * The one way the menu closes. Focus inside the listbox goes back to the trigger first: the
   * listbox is about to unmount, and focus on a removed element falls to the body.
   */
  const closeMenu = useCallback(() => {
    if (menuRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
    focusListOnMountRef.current = false;
    setOpen(false);
  }, []);

  const choose = (next: string) => {
    onChange(next);
    closeMenu();
  };

  /** One step through the rows. Stops at either end rather than wrapping, as a native select does. */
  const step = (delta: 1 | -1) => {
    setActiveIndex((current) => {
      const from = current >= 0 ? current : selectedIndex;
      if (from < 0) return clampRow(delta === 1 ? 0 : rows.length - 1);
      return clampRow(from + delta);
    });
  };

  /*
    The menu is portalled to the body rather than positioned inside this wrapper. Absolute
    positioning was clipped by any scrolling or hidden-overflow ancestor, which is what hid the
    role menu on the last row of the platform admin's users table: `.data-table-wrap` sets
    `overflow-x: auto` (so overflow-y computes to auto too) and `.data-table-card` sets
    `overflow: hidden`, and the menu for the bottom row opens past both. Portalling also lets the
    shared hook flip the menu above the trigger when the viewport has no room below it.
  */
  const { position } = usePopoverPosition({
    triggerRef,
    panelRef: menuRef,
    isOpen: open,
    heightEstimate: (options.length + (clearable ? 1 : 0)) * OPTION_HEIGHT_ESTIMATE,
  });

  // The listbox mounts only once it has a position, which on the first open is a render after
  // `open` flips. A keyboard open waits for it here rather than focusing a node that is not there.
  useEffect(() => {
    if (!open || !position || !focusListOnMountRef.current || !menuRef.current) return;
    focusListOnMountRef.current = false;
    menuRef.current.focus({ preventScroll: true });
  }, [open, position]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // The menu is portalled, so it is not inside `ref`. Both have to be checked or a click on
      // an option would close the menu before it could be chosen.
      if (!ref.current?.contains(target) && !menuRef.current?.contains(target)) closeMenu();
    };
    /*
      ESC closes the menu and stops there; TAB closes it and lets focus move on.

      On `window`, in the CAPTURE phase, and only while the menu is open — all three matter.
      Modals that host a SelectPopover close themselves on their own ESC handler, and those are
      registered on `document` in the capture phase (ConfirmModal, TrustedContactModal). Capture
      runs window → document, so this is the only place a listener can sit and still win; a
      React `onKeyDown` cannot, because React dispatches on the bubble phase, long after a
      document capture listener has already closed the dialog. Without this, one ESC aimed at
      the menu discarded the whole form the coach had just filled in. That holds whether focus is
      on the trigger or in the listbox.

      TAB is closed without stopping propagation or preventing it: focus should still move, but
      leaving the menu floating would put it over whichever field focus lands on — a coach typing
      into an input they cannot see. `closeMenu` moves focus from the listbox to the trigger here,
      before the browser's default Tab action and before any dialog's document-level trap runs, so
      both treat it as a Tab from the trigger. The listbox is portalled outside the dialog; a trap
      that saw focus there would think it had escaped and pull it back to the dialog's first field.
    */
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.stopImmediatePropagation();
        closeMenu();
        triggerRef.current?.focus();
        return;
      }
      if (e.key === "Tab") closeMenu();
    };

    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [open, closeMenu]);

  // Keep the keyboard's row in view: the menu scrolls past eight rows. jsdom has no scrollIntoView.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: "nearest" });
    // optionId is derived from listId, which is stable for the component's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeIndex, position]);

  /**
   * Arrows on the trigger: open the menu from the keyboard, or — when the pointer opened it and
   * focus is still here — step from the current choice and take focus into the listbox. Enter and
   * Space are left to the button's own click, which opens the menu or toggles it shut.
   */
  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!e.repeat) swallowSpaceUpRef.current = false;
    if (disabled) return;
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;

    e.preventDefault();
    const delta = e.key === "ArrowDown" ? 1 : -1;
    if (!open) {
      openFromKeyboard(delta === 1 ? "first" : "last");
      return;
    }
    step(delta);
    if (menuRef.current) menuRef.current.focus({ preventScroll: true });
    else focusListOnMountRef.current = true;
  };

  const handleTriggerKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === " " && swallowSpaceUpRef.current) {
      swallowSpaceUpRef.current = false;
      e.preventDefault();
    }
  };

  /**
   * The listbox's own keys, while it holds focus. Escape and Tab are handled by the window listener
   * above, which has to run before any dialog's own. Handled keys stop here so a dialog's React
   * `onKeyDown` — the listbox is portalled, but React still bubbles through its component tree —
   * does not act on an Enter meant for the menu.
   */
  const handleListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "ArrowDown":
        step(1);
        break;
      case "ArrowUp":
        step(-1);
        break;
      case "Home":
        setActiveIndex(clampRow(0));
        break;
      case "End":
        setActiveIndex(clampRow(rows.length - 1));
        break;
      case "Enter":
      case " ":
        if (e.key === " ") swallowSpaceUpRef.current = true;
        if (activeIndex >= 0 && activeIndex < rows.length) choose(rows[activeIndex]);
        else closeMenu();
        break;
      default:
        return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * A `disabled` control is out of the tab order, which puts any explanation of *why* it is
   * disabled out of reach of the people most likely to need it. When the caller supplies one,
   * present the control as `aria-disabled` instead: it keeps its place in the tab order so the
   * reason is announced, and stays inert because every handler here guards on `disabled`.
   */
  const explainsWhyDisabled = Boolean(disabled && describedById);
  const activeOption =
    activeIndex >= 0 && activeIndex < rows.length ? optionId(activeIndex) : undefined;

  /*
    Chosen on mousedown, which is also when the browser would move focus. Preventing that keeps
    focus where it was — on the trigger after a pointer open — rather than letting it land on the
    listbox as it unmounts and fall to the body.
  */
  const handleOptionMouseDown = (e: React.MouseEvent<HTMLDivElement>, next: string) => {
    e.preventDefault();
    choose(next);
  };

  return (
    <div
      ref={ref}
      className={`sp${fullWidth ? " sp--full-width" : ""}`}
    >
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        data-testid={testId}
        className={`sp__trigger${open ? " sp__trigger--open" : ""}${!value ? " sp__trigger--empty" : ""}${disabled ? " sp__trigger--disabled" : ""}`}
        onClick={(e) => {
          if (disabled) return;
          if (open) {
            closeMenu();
            return;
          }
          // `detail` is 0 for a click the keyboard synthesised (Enter or Space on the closed
          // trigger), which wants a highlighted row to act on and focus in the listbox. A pointer
          // open leaves focus and the rows as they always were.
          if (e.detail === 0) openFromKeyboard("first");
          else openFromPointer();
        }}
        onKeyDown={handleTriggerKeyDown}
        onKeyUp={handleTriggerKeyUp}
        onBlur={() => {
          swallowSpaceUpRef.current = false;
        }}
        disabled={disabled && !explainsWhyDisabled}
        aria-disabled={explainsWhyDisabled || undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-describedby={describedById}
      >
        <span className="sp__label">{value || placeholder}</span>
        <svg
          className={`sp__chevron${open ? " sp__chevron--open" : ""}`}
          width="11"
          height="11"
          viewBox="0 0 11 11"
          fill="none"
          aria-hidden="true"
        >
          <path d="M2 3.5l3.5 3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && position && createPortal(
        <div
          ref={menuRef}
          className="sp__menu"
          role="listbox"
          id={listId}
          tabIndex={-1}
          aria-labelledby={triggerId}
          aria-activedescendant={activeOption}
          onKeyDown={handleListKeyDown}
          style={{
            top: position.top,
            left: position.left,
            minWidth: triggerRef.current?.offsetWidth,
          }}
        >
          {clearable && (
            <div
              id={optionId(0)}
              className={`sp__option sp__option--reset${!value ? " sp__option--selected" : ""}${activeIndex === 0 ? " sp__option--active" : ""}`}
              role="option"
              aria-selected={!value}
              onMouseDown={(e) => handleOptionMouseDown(e, "")}
            >
              {placeholder}
            </div>
          )}
          {options.map((opt, i) => {
            const index = clearable ? i + 1 : i;
            return (
            <div
              key={opt}
              id={optionId(index)}
              className={`sp__option${opt === value ? " sp__option--selected" : ""}${activeIndex === index ? " sp__option--active" : ""}`}
              role="option"
              aria-selected={opt === value}
              onMouseDown={(e) => handleOptionMouseDown(e, opt)}
            >
              {/*
                Label FIRST, tick second. `.sp__check` carries `margin-left: auto`, and an auto
                left margin on the first flex item absorbs all the row's free space — so with the
                tick leading, picking a value shoved that option's text across to the right-hand
                edge of the menu and the list went ragged the moment anything was selected.
              */}
              <span className="sp__option-label">{opt}</span>
              {opt === value && (
                <svg className="sp__check" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};
